/**
 * BAB Account Transaction Modal — the individual bookings behind ONE account.
 *
 * Ported from BabAccountTransactionModal in the BAB Process App, with the filters adapted
 * to this page: the period is fixed by the frozen report, so instead of a year/scope toggle
 * it filters by month, data source and free text.
 *
 * When the live posting detail does not add up to the frozen amount (2025 periods hold
 * migrated aggregates), the API returns the frozen amount per data source instead and
 * `reconciled` is false — the banner then says so rather than showing numbers that
 * contradict the report.
 */
import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, MessageSquare, HelpCircle, AlertTriangle, Search, FileText } from 'lucide-react';
import { babReportApi } from '../../services/babReportApi';
import { useTopmostEscape } from '../../hooks/useTopmostEscape';
import { formatCompanyLabel } from '../../utils/buTheme';
import { formatEur, applySign } from '../../utils/babFormat';
import { buildTransactionRef } from '../../utils/babTransactionRef';
import { SortableHeader, nextSort, type SortState } from '../ui/SortableHeader';
import { BabDocumentViewerModal } from './BabDocumentViewerModal';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import type { BabLineRef, BabAccountRef, BabTransaction, BabTransactionsResponse, BabCommentTarget, SignMode, BabPostingDocument } from '../../types/babReport';

type SortKey = 'postingDate' | 'companyCode' | 'documentNo' | 'description' | 'dataSource' | 'amount';
const NUMERIC_KEYS: SortKey[] = ['amount'];

/** Description gets the lion's share by default — it is the only free-text column and the
 *  reason people open this modal. Everything here is draggable. */
const BADGE_COL_WIDTH = 40;

/** Three badge columns: comment, query, document. */
const BADGE_COL_COUNT = 3;

const COLUMNS: { key: SortKey; label: string; width: number; align: 'left' | 'right' }[] = [
    { key: 'postingDate', label: 'Date', width: 100, align: 'left' },
    { key: 'companyCode', label: 'Company', width: 100, align: 'left' },
    // Was the internal SourceId until 2026-08. That is the surrogate that keys a comment, not
    // the number anyone says out loud - this shows the BC Beleg ('PV-CH2601778'), and the
    // SourceId stays searchable and lives on in the cell tooltip.
    { key: 'documentNo', label: 'Document', width: 150, align: 'left' },
    { key: 'description', label: 'Description', width: 590, align: 'left' },
    { key: 'dataSource', label: 'Data Source', width: 170, align: 'left' },
    { key: 'amount', label: 'Amount EUR', width: 130, align: 'right' },
];

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Same palette as the BAB Process App so the two read alike. */
const DATA_SOURCE_CLASS: Record<string, string> = {
    ErpActual: 'bg-blue-100 text-blue-700',
    BcPosting: 'bg-blue-100 text-blue-700',
    BcAllocation: 'bg-indigo-100 text-indigo-700',
    CalculatedAllocation: 'bg-purple-100 text-purple-700',
    CalculatedStorno: 'bg-orange-100 text-orange-700',
    CalculatedSurcharge: 'bg-teal-100 text-teal-700',
    CalculatedInterest: 'bg-cyan-100 text-cyan-700',
    ManualPosting: 'bg-green-100 text-green-700',
    ManualPostingBAB: 'bg-green-100 text-green-700',
    ManualAllocation: 'bg-lime-100 text-lime-700',
    ManualUpload: 'bg-emerald-100 text-emerald-700',
    ExternalUpload: 'bg-emerald-100 text-emerald-700',
    FixedAnnualAmount: 'bg-yellow-100 text-yellow-700',
    BabReportingMigration: 'bg-gray-200 text-gray-700',
};

function formatDate(value: string | null): string {
    if (!value) return '—';
    return /^\d{4}-\d{2}-\d{2}$/.test(value) ? value.split('-').reverse().join('.') : value;
}

interface Props {
    line: BabLineRef;
    account: BabAccountRef;
    year: number;
    month: number;
    /** Follows the grid's sign toggle — see BabLineAccountModal. */
    signMode: SignMode;
    commentedTxRefs: Set<string>;
    onCommentClick: (target: BabCommentTarget) => void;
    /** Bookings that already carry a Rueckfrage, keyed by the same TransactionRef string. */
    queriedTxRefs: Set<string>;
    onQueryClick: (target: BabCommentTarget) => void;
    /** Booking ringed after jumping here from a comment. */
    highlightTransactionRef?: string | null;
    onClose: () => void;
}

export function BabAccountTransactionModal({
    line, account, year, month, signMode, commentedTxRefs, onCommentClick,
    queriedTxRefs, onQueryClick, highlightTransactionRef, onClose,
}: Props) {
    const [data, setData] = useState<BabTransactionsResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [sort, setSort] = useState<SortState<SortKey>>({ key: null, direction: 'asc' });
    const { widthOf, totalWidthOf, resizingKey, startResize, resetWidth, shouldSuppressClick } =
        useResizableColumns(COLUMNS, 'bab-postings-col-widths');
    const [search, setSearch] = useState('');
    const [months, setMonths] = useState<Set<number>>(new Set());
    const [sources, setSources] = useState<Set<string>>(new Set());
    const [viewing, setViewing] = useState<
        { documents: BabPostingDocument[]; sourceId: string; documentNo: string | null; label: string } | null
    >(null);

    // Esc closes this modal only — the account modal underneath stays open.
    useTopmostEscape(true, onClose);

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError(null);
        babReportApi.getAccountTransactions(year, month, line.lineCode, account.companyCode, account.buCode, account.accountCode)
            .then(res => { if (active) setData(res); })
            .catch(err => { if (active) setError(err instanceof Error ? err.message : 'Failed to load bookings'); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [year, month, line.lineCode, account.companyCode, account.buCode, account.accountCode]);

    const transactions = data?.transactions ?? [];

    const availableMonths = useMemo(() => {
        const set = new Set<number>();
        for (const t of transactions) set.add(t.periodMonth);
        return Array.from(set).sort((a, b) => a - b);
    }, [transactions]);

    const availableSources = useMemo(() => {
        const set = new Set<string>();
        for (const t of transactions) set.add(t.dataSource);
        return Array.from(set).sort();
    }, [transactions]);

    // An empty filter set means "no filter" — chips start unselected so nothing is hidden.
    const filtered = useMemo(() => {
        const needle = search.trim().toLowerCase();
        return transactions.filter(t => {
            if (months.size > 0 && !months.has(t.periodMonth)) return false;
            if (sources.size > 0 && !sources.has(t.dataSource)) return false;
            if (needle) {
                // documentNo is what people paste in from BC; sourceId stays in because it
                // used to be the visible column and someone has it written down.
                const haystack = `${t.description ?? ''} ${t.documentNo ?? ''} ${t.sourceId ?? ''} ${t.accountCode}`.toLowerCase();
                if (!haystack.includes(needle)) return false;
            }
            return true;
        });
    }, [transactions, months, sources, search]);

    const sorted = useMemo(() => {
        if (!sort.key) return filtered;
        const key = sort.key;
        const dir = sort.direction === 'asc' ? 1 : -1;
        return [...filtered].sort((a, b) => {
            let cmp = 0;
            switch (key) {
                case 'postingDate': cmp = (a.postingDate || '').localeCompare(b.postingDate || ''); break;
                case 'companyCode': cmp = a.companyCode.localeCompare(b.companyCode); break;
                case 'documentNo': cmp = (a.documentNo || '').localeCompare(b.documentNo || '', undefined, { numeric: true }); break;
                case 'description': cmp = (a.description || '').localeCompare(b.description || ''); break;
                case 'dataSource': cmp = a.dataSource.localeCompare(b.dataSource); break;
                case 'amount': cmp = a.amountDisplayEUR - b.amountDisplayEUR; break;
            }
            return cmp * dir;
        });
    }, [filtered, sort]);

    const filteredTotal = useMemo(() => sorted.reduce((s, t) => s + t.amountDisplayEUR, 0), [sorted]);
    // See BabLineAccountModal: swallow the click that ends a column drag, or every resize sorts.
    const onSort = (key: SortKey) => {
        if (shouldSuppressClick()) return;
        setSort(s => nextSort(s, key, NUMERIC_KEYS.includes(key)));
    };

    const toggle = <T,>(set: Set<T>, value: T, apply: (next: Set<T>) => void) => {
        const next = new Set(set);
        if (next.has(value)) next.delete(value); else next.add(value);
        apply(next);
    };

    // `line.signage` rather than `data.summary.signage`: identical value (both are MAX(ld.Signage)
    // for this line) but available before the fetch resolves.
    const fmt = (amount: number) => formatEur(applySign(amount, line.signage, signMode));

    const commentTarget = (t: BabTransaction): BabCommentTarget => ({
        level: 'booking',
        lineCode: line.lineCode,
        lineName: line.lineName,
        companyCode: account.companyCode,
        buCode: account.buCode,
        accountCode: account.accountCode,
        accountName: account.accountName,
        transactionRef: buildTransactionRef(t),
        transactionLabel: `${formatDate(t.postingDate)} · ${fmt(t.amountDisplayEUR)} EUR${t.description ? ` · ${t.description}` : ''}`,
    });

    /** Documents for a posting, or an empty array. An absent key is the normal case. */
    const documentsFor = (t: BabTransaction): BabPostingDocument[] =>
        (t.documentNo && data?.documents?.[t.documentNo]) || [];

    const postingLabel = (t: BabTransaction) =>
        `${formatDate(t.postingDate)} · ${fmt(t.amountDisplayEUR)} EUR${t.description ? ` · ${t.description}` : ''}`;

    const numCell = 'px-3 py-1.5 text-right tabular-nums';
    const reconciled = data?.summary.reconciled !== false;

    return createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30" onClick={onClose}>
            <div className="bg-white rounded-lg shadow-xl w-[96vw] max-w-[1700px] max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
                <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                            {account.accountCode}
                            {account.accountName && <span className="text-gray-500 font-normal"> — {account.accountName}</span>}
                            <span className="ml-2 rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 uppercase" title={formatCompanyLabel(account.companyCode)}>
                                {account.companyCode}
                            </span>
                        </h3>
                        <p className="text-sm text-gray-500">
                            Bookings {MONTH_NAMES[0]}–{MONTH_NAMES[month - 1]} {year} · {line.lineName} ({line.lineCode}) · all amounts in EUR
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {!loading && !error && data && !reconciled && (
                    <div className="mx-6 mt-3 flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                        <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                        <span>
                            No individual bookings are available for this period — the frozen figures were migrated,
                            not built from the live posting detail. Showing the frozen amount per data source instead.
                            (Live detail would total {fmt(data.summary.liveTotalEUR)} over {data.summary.livePostingCount} postings
                            vs {fmt(data.summary.frozenTotalEUR)} in the report.)
                        </span>
                    </div>
                )}

                {!loading && !error && transactions.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2 px-6 py-2 border-b border-gray-100 bg-gray-50">
                        <div className="relative">
                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                            <input
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder="Search description or document"
                                className="pl-7 pr-2 py-1 text-xs border rounded-md w-64 bg-white"
                            />
                        </div>
                        {availableMonths.length > 1 && (
                            <div className="flex items-center gap-1">
                                <span className="text-xs font-medium text-gray-500 ml-2">Month</span>
                                {availableMonths.map(m => (
                                    <button
                                        key={m}
                                        onClick={() => toggle(months, m, setMonths)}
                                        className={`px-1.5 py-0.5 rounded text-xs border ${months.has(m)
                                            ? 'bg-blue-600 text-white border-blue-600'
                                            : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-100'}`}
                                    >
                                        {MONTH_NAMES[m - 1]}
                                    </button>
                                ))}
                            </div>
                        )}
                        {availableSources.length > 1 && (
                            <div className="flex items-center gap-1">
                                <span className="text-xs font-medium text-gray-500 ml-2">Source</span>
                                {availableSources.map(s => (
                                    <button
                                        key={s}
                                        onClick={() => toggle(sources, s, setSources)}
                                        className={`px-1.5 py-0.5 rounded text-xs border ${sources.has(s)
                                            ? 'bg-blue-600 text-white border-blue-600'
                                            : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-100'}`}
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                        )}
                        {(search || months.size > 0 || sources.size > 0) && (
                            <button
                                onClick={() => { setSearch(''); setMonths(new Set()); setSources(new Set()); }}
                                className="text-xs text-blue-600 hover:underline ml-1"
                            >
                                Clear filters
                            </button>
                        )}
                    </div>
                )}

                <div className="flex-1 overflow-auto p-6">
                    {loading && (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                            <span className="ml-2 text-gray-500">Loading bookings...</span>
                        </div>
                    )}
                    {error && <p className="text-red-600 text-center py-12">{error}</p>}

                    {!loading && !error && data && (
                        transactions.length === 0 ? (
                            <p className="text-gray-500 text-center py-8">No bookings found for this account.</p>
                        ) : (
                            <div>
                                {/* One booking = one line. Descriptions can be arbitrarily long, so that
                                    one column truncates with the full text on hover instead of forcing
                                    a wide horizontal scroll.

                                    Deliberately NOT `overflow-x-auto`: it would capture the sticky <th>
                                    into an unbounded scroll container and unstick the header. The
                                    enclosing `flex-1 overflow-auto` scrolls both axes. */}
                                <table
                                    className="w-full table-fixed text-sm border-collapse [&_th]:whitespace-nowrap [&_td]:whitespace-nowrap"
                                    style={{ minWidth: totalWidthOf(COLUMNS.map(c => c.key)) + BADGE_COL_COUNT * BADGE_COL_WIDTH }}
                                >
                                    <colgroup>
                                        {COLUMNS.map(c => <col key={c.key} style={{ width: widthOf(c.key) }} />)}
                                        {Array.from({ length: BADGE_COL_COUNT }, (_, i) =>
                                            <col key={i} style={{ width: BADGE_COL_WIDTH }} />)}
                                    </colgroup>
                                    <thead>
                                        <tr className="border-b border-gray-200">
                                            {COLUMNS.map(c => (
                                                <SortableHeader
                                                    key={c.key}
                                                    label={c.label}
                                                    sortKey={c.key}
                                                    sort={sort}
                                                    onSort={onSort}
                                                    align={c.align}
                                                    isResizing={resizingKey === c.key}
                                                    onResizeStart={x => startResize(c.key, x)}
                                                    onResetWidth={() => resetWidth(c.key)}
                                                />
                                            ))}
                                            {Array.from({ length: BADGE_COL_COUNT }, (_, i) =>
                                                <th key={i} className="sticky top-0 z-10 bg-gray-50 px-1 py-2"> </th>)}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {sorted.map((t, i) => {
                                            const ref = buildTransactionRef(t);
                                            const hasComment = commentedTxRefs.has(ref);
                                            const isHighlighted = highlightTransactionRef === ref;
                                            return (
                                                <tr
                                                    key={`${t.companyCode}-${t.periodMonth}-${t.dataSource}-${t.sourceId ?? i}`}
                                                    className={`hover:bg-gray-50 ${!reconciled ? 'italic text-gray-500' : ''} ${isHighlighted ? 'ring-2 ring-inset ring-blue-500 bg-blue-50' : ''}`}
                                                >
                                                    <td className="px-3 py-1.5 whitespace-nowrap">{formatDate(t.postingDate)}</td>
                                                    <td className="px-3 py-1.5">
                                                        <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 uppercase">{t.companyCode}</span>
                                                    </td>
                                                    {/* A dash here is a fact, not a gap: only ErpActual
                                                        postings from 2026 on carry a Beleg. The SourceId
                                                        is still recoverable from the tooltip. */}
                                                    <td
                                                        className="px-3 py-1.5 text-gray-600 whitespace-nowrap truncate"
                                                        title={`Source ID ${t.sourceId ?? '—'}`}
                                                    >
                                                        {t.documentNo ?? '—'}
                                                    </td>
                                                    {/* No `max-w` any more — the colgroup width is the cap,
                                                        so dragging the column genuinely reveals more text. */}
                                                    <td className="px-3 py-1.5 truncate" title={t.description ?? undefined}>
                                                        {t.description ?? '—'}
                                                    </td>
                                                    <td className="px-3 py-1.5">
                                                        <span className={`rounded px-2 py-0.5 text-xs font-medium ${DATA_SOURCE_CLASS[t.dataSource] ?? 'bg-gray-100 text-gray-600'}`}>
                                                            {t.dataSource}
                                                        </span>
                                                    </td>
                                                    {/* Red flags a booking that runs AGAINST its line's
                                                        direction (a credit on a cost account). That is a
                                                        property of the native amount — in `signed` mode
                                                        every cost prints negative and would go red. */}
                                                    <td className={`${numCell} ${t.amountDisplayEUR < 0 ? 'text-red-600' : ''}`}>
                                                        {fmt(t.amountDisplayEUR)}
                                                    </td>
                                                    <td className="px-1 py-1.5 text-center w-10">
                                                        <button
                                                            type="button"
                                                            onClick={e => { e.stopPropagation(); onCommentClick(commentTarget(t)); }}
                                                            className="inline-flex items-center justify-center p-1 rounded hover:bg-black/10"
                                                            title={hasComment ? 'Comment on this booking' : 'Add a comment on this booking'}
                                                        >
                                                            <MessageSquare size={15} className={hasComment ? 'text-blue-600' : 'text-gray-300'} />
                                                        </button>
                                                    </td>
                                                    <td className="px-1 py-1.5 text-center w-10">
                                                        {(() => {
                                                            const docs = documentsFor(t);
                                                            if (docs.length === 0) return null;
                                                            const weak = docs[0].matchConfidence !== null && docs[0].matchConfidence < 100;
                                                            return (
                                                                <button
                                                                    type="button"
                                                                    onClick={e => {
                                                                        e.stopPropagation();
                                                                        setViewing({
                                                                            documents: docs,
                                                                            sourceId: t.sourceId ?? '',
                                                                            documentNo: t.documentNo,
                                                                            label: postingLabel(t),
                                                                        });
                                                                    }}
                                                                    className="relative inline-flex items-center justify-center p-1 rounded hover:bg-black/10"
                                                                    title={docs.length > 1
                                                                        ? `${docs.length} archived documents - open`
                                                                        : weak ? 'Archived document (weak match) - open' : 'Open the archived invoice'}
                                                                >
                                                                    <FileText size={15} className={weak ? 'text-amber-600' : 'text-blue-600'} />
                                                                    {docs.length > 1 && (
                                                                        <span className="absolute -right-0.5 -top-0.5 rounded-full bg-blue-600 px-1 text-[9px] font-semibold leading-tight text-white">
                                                                            {docs.length}
                                                                        </span>
                                                                    )}
                                                                </button>
                                                            );
                                                        })()}
                                                    </td>
                                                    <td className="px-1 py-1.5 text-center w-10">
                                                        <button
                                                            type="button"
                                                            onClick={e => { e.stopPropagation(); onQueryClick(commentTarget(t)); }}
                                                            className="inline-flex items-center justify-center p-1 rounded hover:bg-black/10"
                                                            title={queriedTxRefs.has(ref)
                                                                ? 'Query on this booking'
                                                                : 'Ask a question about this booking'}
                                                        >
                                                            <HelpCircle size={15} className={queriedTxRefs.has(ref) ? 'text-amber-600' : 'text-gray-300'} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                    <tfoot>
                                        <tr className="bg-gray-100 font-semibold border-t-2 border-gray-300">
                                            <td className="px-3 py-1.5" colSpan={5}>
                                                Total ({sorted.length}{sorted.length !== transactions.length ? ` of ${transactions.length}` : ''}
                                                {reconciled ? ` booking${sorted.length === 1 ? '' : 's'}` : ' aggregated rows'})
                                            </td>
                                            <td className={numCell}>{fmt(filteredTotal)}</td>
                                            {Array.from({ length: BADGE_COL_COUNT }, (_, i) => <td key={i} />)}
                                        </tr>
                                    </tfoot>
                                </table>
                                <p className="mt-2 text-xs text-gray-400">
                                    {/* Absent when the caller is the MTD matrix — it cannot know the
                                        YTD total, and a wrong one here is worse than none. */}
                                    {account.actualYTD !== undefined && <>Account total in the report: {fmt(account.actualYTD)} EUR</>}
                                    {sorted.length !== transactions.length && ' · filtered view'}
                                    {/* Only shown where an archive exists: before 2026 the postings
                                        carry no resolvable document number, so the column is blank
                                        for a reason rather than by failure. Companies without
                                        DocuWare say nothing at all — there is nothing to promise. */}
                                    {data.docuWareBaseUrl && year < 2026 &&
                                        ' · Archived documents are available from 2026 on'}
                                </p>
                            </div>
                        )
                    )}
                </div>

                {/* Inside the modal box, not beside it: the viewer portals itself to body, but
                    React events still bubble along the TREE, and the box's stopPropagation is
                    what keeps a click in the viewer from reaching the backdrop and closing the
                    bookings underneath. */}
                {viewing && (
                    <BabDocumentViewerModal
                        documents={viewing.documents}
                        baseUrl={data?.docuWareBaseUrl ?? null}
                        year={year}
                        month={month}
                        lineCode={line.lineCode}
                        companyCode={account.companyCode}
                        buCode={account.buCode}
                        accountCode={account.accountCode}
                        sourceId={viewing.sourceId}
                        documentNo={viewing.documentNo}
                        postingLabel={viewing.label}
                        onClose={() => setViewing(null)}
                    />
                )}
            </div>
        </div>,
        document.body
    );
}
